// Fleet spread — the distribution of per-cab performance, pivoted.
//
// The sibling of Screens.js with a different statistical unit: THE CAB-DAY.
// The founder's observation (2026-09-04) is that a handful of poor cab-days
// sink the fleet mean while most cabs do well — so this screen makes the
// distribution itself the measures: percentiles of the cab-day, laggard
// counts and their ₹ cost against the fleet median, and the day-gate sliced
// as measures (eligible/gated days, ₹ sitting on gated days) instead of
// hiding behind filters.
//
// SAME ARCHITECTURE AS Screens.js, DELIBERATELY: URL is the state, the
// server does every sum (a subtotal's median is the median over the union of
// its atoms — never an average of medians), the table renders the shape the
// payload echoed, and the client never adds two numbers together. Styling is
// Pivot.module.css, unforked.
//
// THE LAGGARD RULE IS PRINTED, NOT IMPLIED: a laggard cab-day earns under
// HALF the median cab-day of the population in view (fleet-wide unless
// filtered), and the strip shows the actual ₹ thresholds the server used.

import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  RefreshCw, Download, SlidersHorizontal, Search, X, Info, ChevronDown, ChevronRight,
} from 'lucide-react';
import { Loading, ErrorState, Pill } from '../../components/ui';
import useApi from '../../lib/useApi';
import api from '../../../services/api';
import Filters from './Filters';
import DateRange, { toISO, addDays, startOfDay } from './DateRange';
import CustomizePanel from './CustomizePanel';
import s from './Pivot.module.css';

const SUB = 'The distribution of per-cab performance, not its average. Every measure describes a set '
  + 'of cab-days: percentiles of what one cab earned in one day, how many days ran under half the '
  + 'fleet median and what they cost, and the payout day-gate as columns — eligible days, gated days, '
  + 'rupees sitting on gated days. Money is the same mart as Screen earnings, so the totals tie; '
  + 'owner attribution is by current owner, like everywhere in insights.';

const DIM_LABEL = {
  media_tenant: 'Media owner', screen: 'Screen', day: 'Day', week: 'Week', month: 'Month',
};
const DIM_HELP = {
  media_tenant: 'The screen’s current owner via the inventory bridge — a distribution per fleet.',
  screen: 'One row per screen: that cab’s own days in the window. Its median day vs the fleet’s says who drags.',
  day: 'IST calendar day: the fleet’s spread on that day. The founder’s lens — a bad mean with a healthy median names its laggards.',
  week: 'IST week (Monday start).',
  month: 'IST calendar month.',
};
const MEA_LABEL = {
  spend: 'Earned ₹', spend_all: 'Earned ₹ (all plays)', days: 'Cab-days', cabs: 'Cabs',
  mean_day: 'Mean day', p10: 'p10 day', p25: 'p25 day', p50: 'Median day', p75: 'p75 day', p90: 'p90 day',
  best_day: 'Best day', worst_day: 'Worst day',
  lag_days: 'Laggard days', lag_gap: 'Laggard cost ₹', zero_days: 'Zero days', top_decile_share: 'Top-decile share',
  eligible_days: 'Days eligible', gated_days: 'Days gated', unjudged_days: 'Days unjudged',
  gate_pass_pct: 'Gate pass', spend_gated_days: '₹ on gated days', spend_eligible_days: '₹ on eligible days',
  trips: 'Trips', travel_hours: 'Driving hours', presence_hours: 'Passenger hours',
  rev_per_drive_hr: '₹ / driving hour', rev_per_trip: '₹ / trip',
  p50_disc: 'Median day (an actual cab)', median_cab: 'Median cab-day · who',
  best_cab: 'Best cab-day · who', worst_cab: 'Worst cab-day · who',
  cab_typical: 'Typical cab · median day', cab_p10: 'Weak cab · median day', cab_p90: 'Strong cab · median day',
  chronic_lag_cabs: 'Chronic laggard cabs', episodic_lag_cabs: 'Episodic laggard cabs', top_cab_share: 'Top-cab share',
};
const MEA_HELP = {
  spend: 'Rupees earned under the presence gate — the same basis as Screen earnings, so the totals tie between the two pages.',
  spend_all: 'Rupees over all plays, ignoring the gate.',
  days: 'Cab-days in this row — the atoms every distribution measure is computed over.',
  cabs: 'Distinct screens contributing at least one day to this row.',
  mean_day: 'The average cab-day. When this sits well below the median, a few bad days are dragging — that is the whole point of this page.',
  p10: '10% of this row’s cab-days earned less than this. The tail that drags the mean.',
  p25: 'A quarter of cab-days earned less than this.',
  p50: 'The middle cab-day — the honest “typical day”. Compare against Mean day to see the drag.',
  p75: 'Three quarters of cab-days earned less than this.',
  p90: 'The strong end: 10% of cab-days earned more than this.',
  best_day: 'The single best cab-day in this row.',
  worst_day: 'The single worst cab-day in this row.',
  lag_days: 'Cab-days earning under HALF the median cab-day of the population in view. The threshold ₹ is printed in the strip below.',
  lag_gap: 'What those laggard days cost versus a median day, in rupees — the size of the drag, not just its count.',
  zero_days: 'Cab-days that earned nothing at all. A subset of laggard days; usually a device or deployment problem, not a demand problem.',
  top_decile_share: 'Share of this row’s rupees earned by cab-days at or above the fleet’s p90. High concentration = the fleet leans on its best days.',
  eligible_days: 'Cab-days that PASSED the payout day-gate (frozen verdicts from the payout plane).',
  gated_days: 'Cab-days that FAILED the gate — their supply share went unpaid.',
  unjudged_days: 'Cab-days with no frozen verdict yet (usually today, or pre-freeze history).',
  gate_pass_pct: 'Eligible over judged days. The same gate the payout screens enforce, read here as a measure.',
  spend_gated_days: 'Rupees that landed on gate-FAILED days — earned for the platform, unpaid to the fleet. The money the gate is actually withholding, sliceable by any breakdown.',
  spend_eligible_days: 'Rupees on gate-passed days.',
  trips: 'Passenger trips.',
  travel_hours: 'Hours driving.',
  presence_hours: 'Hours with a passenger verified aboard.',
  rev_per_drive_hr: 'Rupees per driving hour, from this row’s own sums — the efficiency read.',
  rev_per_trip: 'Rupees per trip, from this row’s own sums.',
  p50_disc: 'The median as an OBSERVED value: always one of the row\u2019s actual cab-days (percentile_disc), never an interpolation between two cabs. With an odd count it equals Median day; with an even count Median day is a midpoint that matches nobody, and this is the real cab-day just below it.',
  median_cab: 'WHO the median was: the cab (and day) sitting at the middle of this row\u2019s cab-days. The summary, with its name attached.',
  best_cab: 'The single best cab-day in this row, by name and date.',
  worst_cab: 'The single worst cab-day in this row, by name and date \u2014 ties broken deterministically, zero-days first.',
  cab_typical: 'THE CAB IS THE UNIT here, not the cab-day: each cab is first summarized by its own median day, then this is the median ACROSS CABS. A cab running 10 days no longer outweighs one running 2. Sitting above the day-basis median means weak days concentrate in a few cabs rather than spreading evenly.',
  cab_p10: 'The weak end of the fleet, cab by cab: 10% of cabs have a typical (median) day below this.',
  cab_p90: 'The strong end: 10% of cabs have a typical day above this.',
  chronic_lag_cabs: 'Cabs whose TYPICAL (median) day is under the laggard bar — consistently weak performers, not unlucky ones. These are the names to act on.',
  episodic_lag_cabs: 'Cabs whose typical day clears the bar but who logged at least one laggard day — decent performers with bad days. A device or deployment hiccup, usually, not a cab problem.',
  top_cab_share: 'Share of this row’s rupees earned by its single best cab. High = the row leans on one vehicle.',
};

const DIM_SECTIONS = [
  { id: 'level', label: 'Level', keys: ['media_tenant', 'screen'] },
  { id: 'time', label: 'Time', keys: ['day', 'week', 'month'] },
];
const MEA_SECTIONS = [
  { id: 'money', label: 'Money', keys: ['spend', 'spend_all', 'days', 'cabs'] },
  { id: 'spreadf', label: 'The spread', keys: ['p10', 'p25', 'p50', 'p75', 'p90', 'mean_day', 'best_day', 'worst_day', 'top_decile_share'] },
  { id: 'named', label: 'Named representatives', keys: ['p50_disc', 'median_cab', 'best_cab', 'worst_cab'] },
  { id: 'laggards', label: 'Laggards', keys: ['lag_days', 'lag_gap', 'zero_days'] },
  { id: 'bycab', label: 'By cab (unit = the cab)', keys: ['cab_typical', 'cab_p10', 'cab_p90', 'chronic_lag_cabs', 'episodic_lag_cabs', 'top_cab_share'] },
  { id: 'gate', label: 'The day-gate', keys: ['eligible_days', 'gated_days', 'unjudged_days', 'gate_pass_pct', 'spend_gated_days', 'spend_eligible_days'] },
  { id: 'mobility', label: 'Mobility & efficiency', keys: ['trips', 'travel_hours', 'presence_hours', 'rev_per_drive_hr', 'rev_per_trip'] },
];

// Frozen key columns: uniform width so each column's sticky `left` offset is
// deterministic. 190px holds a thing_name + id chip without wrapping.
const KEY_W = 190;
const frozenStyle = (i) => ({ left: i * KEY_W, minWidth: KEY_W, maxWidth: KEY_W, width: KEY_W });

const VIEWS = [
  { id: 'pivot', label: 'Pivot', help: 'Rows nest with subtotals; a subtotal’s percentiles are computed over the union of its cab-days, never averaged from child rows.' },
  { id: 'flat', label: 'Ungrouped', help: 'One row per combination of breakdowns, no subtotals.' },
];

const MONEY_KEYS = new Set(['spend', 'spend_all', 'mean_day', 'p10', 'p25', 'p50', 'p75', 'p90',
  'best_day', 'worst_day', 'lag_gap', 'spend_gated_days', 'spend_eligible_days', 'rev_per_drive_hr', 'rev_per_trip',
  'cab_typical', 'cab_p10', 'cab_p90']);
const PCT_KEYS = new Set(['gate_pass_pct', 'top_decile_share', 'top_cab_share']);
const TEXT_KEYS = new Set(['median_cab', 'best_cab', 'worst_cab']);

const n = (v) => (v == null ? '—' : Number(v).toLocaleString('en-IN'));
const inr = (v, dp = 2) => (v == null ? '—'
  : `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`);
const fmtCell = (key, v) => {
  if (v == null) return '—';
  if (TEXT_KEYS.has(key)) return String(v);
  if (key === 'p50_disc') return inr(v);
  if (MONEY_KEYS.has(key)) return inr(v);
  if (PCT_KEYS.has(key)) return `${v}%`;
  if (key === 'travel_hours' || key === 'presence_hours') return Number(v).toLocaleString('en-IN', { maximumFractionDigits: 1 });
  return n(v);
};
const fmtDay = (iso) => {
  const [y, m, d] = String(iso || '').split('-').map(Number);
  return y && m && d
    ? new Date(y, m - 1, d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';
};
const fmtClock = (ts) => (ts
  ? new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
  : 'never');
const fmtKey = (dim, v) => {
  if (v == null) return null;
  if (dim === 'day' || dim === 'week' || dim === 'month') return String(v).slice(0, 10);
  return String(v);
};

function InfoDot({ title }) {
  return (
    <span className={s.dot} tabIndex={0} role="img" aria-label={title} title={title}>
      <Info size={11} aria-hidden="true" />
    </span>
  );
}

function buildTree(rows, dims) {
  const pathAt = (r, d) => dims.slice(0, d).map((k) => `${k}=${r.keys[k] ?? ' '}`).join('/');
  const byPath = new Map();
  const roots = [];
  for (const r of rows) {
    const depth = r.depth == null ? dims.length : r.depth;
    const path = pathAt(r, depth);
    const node = { ...r, depth, path, leaf: depth >= dims.length, children: [] };
    byPath.set(path, node);
    const parent = depth > 1 ? byPath.get(pathAt(r, depth - 1)) : null;
    (parent ? parent.children : roots).push(node);
  }
  return roots;
}
function flattenTree(nodes, collapsed, out = []) {
  for (const node of nodes) {
    out.push(node);
    if (node.children.length && !collapsed.has(node.path)) flattenTree(node.children, collapsed, out);
  }
  return out;
}

export default function Spread() {
  const [params, setParams] = useSearchParams();
  const readList = useCallback(
    (k) => (params.get(k) ? params.get(k).split(',').filter(Boolean) : []), [params]);

  const today = useMemo(() => startOfDay(new Date()), []);
  const range = useMemo(() => ({
    from: params.get('from') || toISO(addDays(today, -29)),
    to: params.get('to') || toISO(today),
  }), [params, today]);

  const breakdowns = useMemo(() => {
    const b = readList('breakdowns');
    return b.length ? b : ['day'];
  }, [readList]);
  const measures = useMemo(() => {
    const m = readList('measures');
    return m.length ? m : ['spend', 'p50', 'mean_day', 'p90', 'p10', 'lag_days', 'lag_gap', 'gate_pass_pct'];
  }, [readList]);
  const filters = useMemo(() => ({
    media_tenants: readList('media_tenants'),
    screens: readList('screens'),
  }), [readList]);
  const q = params.get('q') || '';
  const view = params.get('view') === 'flat' ? 'flat' : 'pivot';

  const patch = useCallback((obj) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(obj)) {
      if (Array.isArray(v)) { if (v.length) p.set(k, v.join(',')); else p.delete(k); }
      else if (v) p.set(k, v); else p.delete(k);
    }
    setParams(p, { replace: true });
  }, [params, setParams]);

  const qs = useMemo(() => {
    const p = new URLSearchParams({
      from: range.from, to: range.to, view,
      breakdowns: breakdowns.join(','), measures: measures.join(','),
    });
    for (const k of ['media_tenants', 'screens']) {
      if (filters[k].length) p.set(k, filters[k].join(','));
    }
    if (q) p.set('q', q);
    return p.toString();
  }, [range, breakdowns, measures, filters, q, view]);

  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [customizeTab, setCustomizeTab] = useState('breakdowns');
  const [collapsed, setCollapsed] = useState(() => new Set());
  const shape = `${breakdowns.join(',')}|${view}`;
  useEffect(() => { setCollapsed(new Set()); }, [shape]);

  const [draft, setDraft] = useState(q);
  const lastCommitted = useRef(q);
  useEffect(() => {
    if (q !== lastCommitted.current) { lastCommitted.current = q; setDraft(q); }
  }, [q]);
  useEffect(() => {
    if (draft.trim() === q) return undefined;
    const t = setTimeout(() => { lastCommitted.current = draft.trim(); patch({ q: draft.trim() }); }, 300);
    return () => clearTimeout(t);
  }, [draft, q, patch]);

  const { data, error, loading, reload } = useApi(
    () => api.get(`/insights/spread?${qs}`).then((r) => {
      // A 200 that is not this route's envelope (an HTML fallthrough from a
      // server older than the client) must be a readable error, never a
      // silently empty table with a dead Customize button — the payout
      // screens learned this on 3 Sep; this screen keeps the lesson.
      const d = r.data?.data;
      if (!d || typeof d !== 'object' || !Array.isArray(d.rows)) {
        throw new Error(
          'The server did not answer with the Fleet spread payload. If this is a local dev '
          + 'session, the iot-admin server is probably older than the client — restart it '
          + '(Ctrl+C, then ./scripts/dev-iot-admin.sh).');
      }
      return d;
    }), [qs]);

  const first = loading && !data;
  const vocabulary = data?.vocabulary;
  const dimensions = useMemo(
    () => (vocabulary?.dimensions || []).map((d) => ({ key: d.key, label: DIM_LABEL[d.key] || d.label })),
    [vocabulary],
  );
  const measureDefs = useMemo(
    () => (vocabulary?.measures || []).map((m) => ({ key: m.key, label: MEA_LABEL[m.key] || m.label })),
    [vocabulary],
  );

  const toggle = (list, v, key, max) => {
    const next = list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
    if (max && next.length > max) return;
    if (!next.length) return;
    patch({ [key]: next });
  };

  const viewBreakdowns = data?.breakdowns?.length ? data.breakdowns : breakdowns;
  const viewMeasures = data?.measures?.length ? data.measures : measures;
  const viewMode = data ? (data.view || 'flat') : view;

  const rows = useMemo(() => data?.rows || [], [data]);
  const visible = useMemo(() => {
    if (!rows.length) return [];
    if (viewMode === 'flat') return rows.map((r, i) => ({ ...r, path: `f${i}`, children: [] }));
    return flattenTree(buildTree(rows, viewBreakdowns), collapsed);
  }, [rows, viewBreakdowns, viewMode, collapsed]);

  const toggleNode = (path) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(path)) next.delete(path); else next.add(path);
    return next;
  });

  const exportCsv = () => {
    if (!data?.rows) return;
    const cell = (v) => {
      if (v == null) return '';
      const t = String(v);
      return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
    };
    const labelled = viewBreakdowns.filter((b) => data.rows.some((r) => r.labels?.[b]));
    const keyCols = viewBreakdowns.flatMap((b) => (labelled.includes(b)
      ? [DIM_LABEL[b] || b, `${DIM_LABEL[b] || b} id`] : [DIM_LABEL[b] || b]));
    const keyVals = (r) => viewBreakdowns.flatMap((b) => {
      const has = Object.prototype.hasOwnProperty.call(r.keys, b);
      const v = !has ? '' : (r.keys[b] == null ? 'unassigned' : fmtKey(b, r.keys[b]));
      return labelled.includes(b) ? [has ? (r.labels?.[b] || '') : '', v] : [v];
    });
    const lead = viewMode === 'pivot' ? ['Level'] : [];
    const leadVals = (r) => (viewMode === 'pivot'
      ? [(r.depth ?? viewBreakdowns.length) >= viewBreakdowns.length ? 'row'
        : `subtotal:${DIM_LABEL[viewBreakdowns[r.depth - 1]] || viewBreakdowns[r.depth - 1]}`]
      : []);
    const cols = [...lead, ...keyCols,
      ...viewMeasures.map((m) => MEA_LABEL[m] || m)];
    const lines = [cols.map(cell).join(',')];
    for (const r of data.rows) {
      lines.push([...leadVals(r), ...keyVals(r), ...viewMeasures.map((m) => r[m])].map(cell).join(','));
    }
    lines.push([...[...lead, ...keyCols].map((_, i) => (i ? '' : 'TOTAL (all matching cab-days)')),
      ...viewMeasures.map((m) => data.totals?.[m])].map(cell).join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `fleet-spread_${range.from}_${range.to}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 0);
  };

  const totals = data?.totals || {};
  const baseline = data?.fleet_baseline;
  const facets = data?.facets;
  const truncated = data?.truncated;
  const source = data?.source;
  const colCount = viewBreakdowns.length + viewMeasures.length;

  const filterFields = useMemo(() => (facets ? [
    { key: 'media_tenants', label: 'Media owner', chipNoun: 'owner',
      options: (facets.media_tenants || []).map((t) => ({ value: t.id, label: t.name || t.id || 'unassigned' })) },
    { key: 'screens', label: 'Screen', chipNoun: 'screen',
      options: (facets.screens || []).map((sc) => ({ value: sc.id, label: sc.thing_name || sc.id })) },
  ] : null), [facets]);

  return (
    <div className={s.page} data-fill>
      <header className={s.head}>
        <h1 className={s.h1}>Fleet spread</h1>
        <InfoDot title={SUB} />

        <div className={s.viewToggle} role="group" aria-label="Table view">
          {VIEWS.map((v) => (
            <button key={v.id} type="button" title={v.help}
                    aria-pressed={viewMode === v.id}
                    className={`${s.viewBtn} ${viewMode === v.id ? s.viewBtnOn : ''}`}
                    onClick={() => patch({ view: v.id === 'pivot' ? '' : v.id })}>
              {v.label}
            </button>
          ))}
        </div>

        <span className={s.spacer} />

        <button type="button" className={s.btn} onClick={reload} disabled={loading}
                aria-busy={loading}
                title="Re-run the query. The mart refreshes hourly; this re-reads it.">
          <RefreshCw size={13} className={loading ? s.spin : undefined} aria-hidden="true" />
          Refresh
        </button>
        <button type="button" className={s.btn} onClick={exportCsv} disabled={!rows.length}
                title="Download these rows plus the server's totals as CSV, exactly as returned.">
          <Download size={13} aria-hidden="true" />
          Export
        </button>
        <button type="button" aria-pressed={customizeOpen}
                className={`${s.btn} ${customizeOpen ? s.btnOn : ''}`}
                onClick={() => setCustomizeOpen((o) => !o)}
                title="Choose breakdowns and measures — the spread, laggard and gate families live here.">
          <SlidersHorizontal size={13} aria-hidden="true" />
          Customize
        </button>
      </header>

      <div className={s.controls}>
        <div className={s.searchWrap}>
          <Search size={13} className={s.searchIcon} aria-hidden="true" />
          <input
            type="search" className={s.searchInput} value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Search screen or owner"
            aria-label="Search rows"
            title="Filters on the SERVER, so totals AND the laggard baseline move with it — a filtered view judges laggards against its own median."
          />
          {draft && (
            <button type="button" className={s.searchClear} aria-label="Clear search"
                    onClick={() => setDraft('')}>
              <X size={12} />
            </button>
          )}
        </div>

        {facets && filterFields && (
          <Filters compact facets={facets} fields={filterFields} value={filters}
                   onChange={(v) => patch(v)} summary={null} />
        )}

        <span className={s.spacer} />
        <DateRange from={range.from} to={range.to}
                   onChange={(f, t) => patch({ from: f, to: t })} />
      </div>

      {/* STOP CONDITIONS above the table; quiet confirmations in the strip
          below it — the Screens doctrine, applied to this dataset. */}
      {data?.parity && !data.parity.in_parity && (
        <div className={s.parityBad}>
          <Pill tone="bad">OUT OF PARITY</Pill>
          <span className={s.parityText}>
            These numbers DISAGREE with the live derivation on settled days:
            {' '}&Delta;{inr(data.parity.spend.delta)} · &Delta;{n(data.parity.plays.delta)} plays.
            Do not use this view until it is resolved.
          </span>
        </div>
      )}
      {data?.rollup_agrees === false && (
        <div className={s.parityBad}>
          <Pill tone="bad">SUBTOTALS DISAGREE</Pill>
          <span className={s.parityText}>
            The grouped query&rsquo;s grand total does not match the independent ungrouped one
            ({(data.rollup_mismatched || []).join(', ')}). Switch to Ungrouped and report this.
          </span>
        </div>
      )}
      {data?.integrity && data.integrity.violation_count > 0 && (
        <div className={s.parityBad}>
          <Pill tone="bad">IDENTITY VIOLATIONS</Pill>
          <span className={s.parityText}>
            {n(data.integrity.violation_count)} of {n(data.integrity.checks)} internal identities
            failed on this view (e.g. {data.integrity.violations[0]?.rule}). A percentile ordering
            or partition equality is broken — do not trust these rows; report this.
          </span>
        </div>
      )}
      {truncated && (
        <div className={s.truncated}>
          Showing the first {n(vocabulary?.max_rows)} rows. The totals row is computed over every
          matching cab-day, so it stays correct — add a filter or drop a breakdown to see them all.
        </div>
      )}

      <div className={s.body}>
        <div className={s.main}>
          <div className={`${s.tableWrap} ${loading && data ? s.stale : ''}`}>
            {first && <Loading what="the fleet spread" />}
            {!first && error && <ErrorState error={error} onRetry={reload} />}
            {!first && !error && (
              <table className={s.table}>
                <thead>
                  <tr>
                    {viewBreakdowns.map((b, i) => (
                      <th key={b}
                          className={`${s.hKey} ${s.frozen} ${i === viewBreakdowns.length - 1 ? s.frozenEdge : ''}`}
                          style={frozenStyle(i)}>
                        {DIM_LABEL[b] || b}
                        <InfoDot title={DIM_HELP[b]} />
                      </th>
                    ))}
                    {viewMeasures.map((m) => (
                      <th key={m} className={s.hNum}>
                        {MEA_LABEL[m] || m}
                        <InfoDot title={MEA_HELP[m]} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((node) => {
                    const isSub = viewMode === 'pivot' && !node.leaf;
                    const open = !collapsed.has(node.path);
                    return (
                      <tr key={node.path} className={isSub ? s.subRow : undefined}>
                        {viewBreakdowns.map((b, i) => {
                          const frozenCls = `${s.frozen} ${i === viewBreakdowns.length - 1 ? s.frozenEdge : ''}`;
                          const show = viewMode === 'flat' || i === node.depth - 1;
                          if (!show) return <td key={b} className={`${s.key} ${frozenCls}`} style={frozenStyle(i)} />;
                          const label = node.labels?.[b] || fmtKey(b, node.keys[b]);
                          const id = node.keys[b];
                          return (
                            <th key={b} scope="row" className={`${s.key} ${frozenCls}`}
                                style={frozenStyle(i)}
                                title={node.labels?.[b] ? `${node.labels[b]} · ${id}` : undefined}>
                              <span className={s.keyBody}>
                                {viewMode === 'pivot' && node.children.length > 0 ? (
                                  <button type="button" className={s.chev}
                                          aria-expanded={open}
                                          aria-label={`${open ? 'Collapse' : 'Expand'} ${label || 'unassigned'}`}
                                          onClick={() => toggleNode(node.path)}>
                                    {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                  </button>
                                ) : viewMode === 'pivot' && i === 0 && viewBreakdowns.length > 1
                                  ? <span className={s.chevGap} /> : null}
                                {label == null
                                  ? <span className={s.null} title="Cab-days whose owner does not resolve — kept, so totals stay whole.">unassigned</span>
                                  : <span className={s.keyName}>{label}</span>}
                                {node.labels?.[b] && id != null && (
                                  <span className={s.keyId}>{String(id).slice(0, 8)}</span>
                                )}
                              </span>
                            </th>
                          );
                        })}
                        {viewMeasures.map((m) => (
                          <td key={m} className={s.num}
                              style={TEXT_KEYS.has(m) ? { textAlign: 'left', fontVariantNumeric: 'normal' } : undefined}>
                            {fmtCell(m, node[m])}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                  {!visible.length && (
                    <tr><td colSpan={colCount} className={s.empty}>
                      {q
                        ? <>Nothing in this window matches &ldquo;{q}&rdquo;.{' '}
                          <button type="button" className={s.linkBtn} onClick={() => setDraft('')}>
                            Clear the search
                          </button> to see every row.</>
                        : 'Nothing matches this window and these filters.'}
                    </td></tr>
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan={viewBreakdowns.length} scope="row"
                        className={`${s.totalLabel} ${s.frozen} ${s.frozenEdge}`}
                        style={{ left: 0, minWidth: KEY_W * viewBreakdowns.length }}>
                      Whole window
                      <InfoDot title="Computed over every matching cab-day by its own query — the fleet-level distribution, not a sum of the rows on screen." />
                    </th>
                    {viewMeasures.map((m) => (
                      <td key={m} className={`${s.num} ${s.totalCell}`}
                          style={TEXT_KEYS.has(m) ? { textAlign: 'left', fontVariantNumeric: 'normal' } : undefined}>
                        {fmtCell(m, totals[m])}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            )}
          </div>

          {data && (
            <div className={s.strip}>
              <span title="IST calendar buckets, the same ones billing and payouts settle on.">
                {fmtDay(range.from)} – {fmtDay(range.to)} · Indian Standard Time
              </span>
              <span title="Cab-days behind this view — the atoms every percentile and laggard measure is computed over.">
                {n(baseline?.atoms)} cab-days · {n(visible.length)} of {n(rows.length)}{truncated ? '+' : ''} rows
              </span>
              {data?.parity?.in_parity && (
                <span className={s.ok}
                      title="Settled days in this window match the live derivation exactly — same plays, same rupees, with this view's own filters re-applied on both sides. Checked on every request. Today (and the not-yet-settled tail) is reported beside it, never asserted.">
                  Reconciled · {n(data.parity.plays.mart)} plays, {inr(data.parity.spend.mart)} on settled days
                  {(data.parity.open_day.plays > 0 || data.parity.open_day.spend > 0)
                    && <> · open +{n(data.parity.open_day.plays)} plays, {inr(data.parity.open_day.spend)}</>}
                </span>
              )}
              {data?.integrity && data.integrity.violation_count === 0 && (
                <span className={s.ok}
                      title="Every row in this exact view — every breakdown combination — passed its internal identities: percentile orderings (worst ≤ p10 ≤ … ≤ p90 ≤ best), gate-day partitions summing to the day count, laggard and cab-count bounds. Re-checked on every request; with the grouped grand total verified against an independent ungrouped query.">
                  {n(data.integrity.checks)} identities checked · 0 violations{data.rollup_agrees ? ' · subtotals verified' : ''}
                </span>
              )}
              {data?.regime && (
                <span title="Cab-day rupees are priced by the regime that governed each day: the billing predicate before 1 Aug 2026, presence-verified money from it. The settled-days parity above stays on the blanket accrual — books are never filtered.">
                  {data.regime.note}
                </span>
              )}
              {baseline && (
                <span className={s.ok}
                      title={`${baseline.laggard_rule}. Both thresholds are computed over the population in view — filter to one fleet and laggards are judged against that fleet's own median.`}>
                  Baseline: median day {inr(baseline.median_day)} · laggard under {inr(baseline.median_day / 2)} · strong day {inr(baseline.p90_day)}+
                </span>
              )}
              <span className={s.updated}
                    title="When the money mart was last refreshed, not when this page loaded.">
                Updated {fmtClock(source?.refreshed_at)}
              </span>
            </div>
          )}
        </div>

        {customizeOpen && vocabulary && (
          <CustomizePanel
            tab={customizeTab}
            onTab={setCustomizeTab}
            dimensions={dimensions}
            measures={measureDefs}
            dimensionSections={DIM_SECTIONS}
            measureSections={MEA_SECTIONS}
            selectedBreakdowns={breakdowns}
            selectedMeasures={measures}
            onToggleBreakdown={(k) => toggle(breakdowns, k, 'breakdowns', vocabulary.max_breakdowns)}
            onToggleMeasure={(k) => toggle(measures, k, 'measures')}
            onClose={() => setCustomizeOpen(false)}
            maxBreakdowns={vocabulary.max_breakdowns}
            help={{ ...DIM_HELP, ...MEA_HELP }}
            footNote={(
              <>
                The unit is the CAB-DAY; a subtotal&rsquo;s percentiles are computed over the union of
                its cab-days, never averaged. Laggard thresholds are printed in the strip.
              </>
            )}
          />
        )}
      </div>
    </div>
  );
}
